<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { getDeviceSettings, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Services
	import { format } from 'svelte-i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	//Helpers
	import { real_id } from '../../../../helpers/interface_helpers';
	//Types
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	import type { WallSwitchUIConfig, WallSwitchUIOption } from 'src/types/wall_switch_ui';
	//Design system components
	import { Button, Input, Select, Text, Toggle, toast } from '@Shelly/shelly-design-system';

	export let device: Device;

	const BRIGHTNESS_MIN = 0;
	const BRIGHTNESS_MAX = 100;
	const DEFAULT_BRIGHTNESS = 100;

	// Raw WALLSWITCH_UI.GetConfig enum values in display order, paired with their
	// i18n label keys. Keeping the device enums in named constants avoids scattering
	// magic strings across the component and keeps value <-> label mapping in one place.
	const LED_MODE_OPTIONS: WallSwitchUIOption[] = [
		{ value: 'disabled', labelKey: 'option_ss_led_mode_disabled', labelDefault: 'Off' },
		{
			value: 'system_status',
			labelKey: 'option_ss_led_mode_system_status',
			labelDefault: 'System status',
		},
		{
			value: 'switch_status',
			labelKey: 'option_ss_led_mode_switch_status',
			labelDefault: 'Switch status',
		},
		{
			value: 'system_and_switch_status',
			labelKey: 'option_ss_led_mode_system_and_switch_status',
			labelDefault: 'System and switch status',
		},
		{ value: 'always_on', labelKey: 'option_ss_led_mode_always_on', labelDefault: 'Always on' },
	];

	const LED_INDICATION_OPTIONS: WallSwitchUIOption[] = [
		{
			value: 'on_when_output_on',
			labelKey: 'option_ss_led_output_on',
			labelDefault: 'On when the output is on',
		},
		{
			value: 'on_when_output_off',
			labelKey: 'option_ss_led_output_off',
			labelDefault: 'On when the output is off',
		},
	];

	const id = real_id(device.id);

	const ledModeValues = LED_MODE_OPTIONS.map((option) => option.value);
	const ledModeLabels = LED_MODE_OPTIONS.map((option) =>
		$format(option.labelKey, { default: option.labelDefault }),
	);
	const ledIndicationValues = LED_INDICATION_OPTIONS.map((option) => option.value);
	const ledIndicationLabels = LED_INDICATION_OPTIONS.map((option) =>
		$format(option.labelKey, { default: option.labelDefault }),
	);

	// Fills the form locals and their caches from a wallswitch_ui payload. Runs on
	// first paint and once more if the payload only arrives after mount, so the
	// form reflects the device instead of staying on empty/default values.
	const applyWallSwitchUi = (cfg: WallSwitchUIConfig) => {
		ledModeLabel = ledModeLabels[ledModeValues.indexOf(cfg.led_mode ?? '')];
		_ledModeVal = ledModeValues[ledModeLabels.indexOf(ledModeLabel)] ?? cfg.led_mode;
		cacheLedModeVal = _ledModeVal;

		ledIndicationLabel =
			ledIndicationLabels[ledIndicationValues.indexOf(cfg.led_output_indication ?? '')];
		_ledIndicationVal =
			ledIndicationValues[ledIndicationLabels.indexOf(ledIndicationLabel)] ??
			cfg.led_output_indication;
		cacheLedIndicationVal = _ledIndicationVal;

		brightness =
			typeof cfg.led_brightness === 'number'
				? String(cfg.led_brightness)
				: String(DEFAULT_BRIGHTNESS);
		cacheBrightness = Number(brightness);

		swapInputs = !!cfg.swap_inputs;
		cacheSwapInputs = swapInputs;
	};

	let settings = readDeviceSettings(id);

	let ledModeLabel: string;
	let _ledModeVal: string;
	let cacheLedModeVal: string;

	let ledIndicationLabel: string;
	let _ledIndicationVal: string;
	let cacheLedIndicationVal: string;

	// Kept as a string because it is bound to a text-like number Input; parsed via
	// `brightnessNum` for validation and before sending to the device.
	let brightness: string;
	let cacheBrightness: number;

	let swapInputs: boolean;
	let cacheSwapInputs: boolean;

	let formHydrated = !!settings?.wallswitch_ui;
	let loading = false;

	applyWallSwitchUi(settings?.wallswitch_ui ?? {});

	$: settingsStore = getDeviceSettings(id);
	$: settings = $settingsStore ?? settings;
	$: config = settings?.wallswitch_ui ?? {};

	// One-shot hydrate: settings is reactive but the form locals are not, so a
	// wallswitch_ui payload that lands after mount would otherwise never fill the
	// form. Re-apply exactly once when it first appears, then leave user edits.
	$: if (!formHydrated && settings?.wallswitch_ui) {
		applyWallSwitchUi(config);
		formHydrated = true;
	}

	$: brightnessNum = Number(brightness);
	$: brightnessInvalid =
		brightness === '' ||
		Number.isNaN(brightnessNum) ||
		!Number.isInteger(brightnessNum) ||
		brightnessNum < BRIGHTNESS_MIN ||
		brightnessNum > BRIGHTNESS_MAX;
	$: brightnessError = brightnessInvalid
		? $format('ss_led_brightness_error', {
				values: { min: BRIGHTNESS_MIN, max: BRIGHTNESS_MAX },
				default: 'Enter a whole number between 0 and 100.',
			})
		: '';
	$: brightnessInfo = $format('ss_led_brightness_hint', {
		values: { min: BRIGHTNESS_MIN, max: BRIGHTNESS_MAX },
		default: 'Value between 0 and 100.',
	});

	const changeLedMode = (e: CustomEvent<string>) => {
		ledModeLabel = e.detail;
		_ledModeVal = ledModeValues[ledModeLabels.indexOf(e.detail)] ?? cacheLedModeVal;
	};

	const changeLedIndication = (e: CustomEvent<string>) => {
		ledIndicationLabel = e.detail;
		_ledIndicationVal =
			ledIndicationValues[ledIndicationLabels.indexOf(e.detail)] ?? cacheLedIndicationVal;
	};

	const sentRequests = async () => {
		if (brightnessInvalid) return;

		const changedConfig: Record<string, unknown> = {};
		if (_ledModeVal !== cacheLedModeVal) changedConfig.led_mode = _ledModeVal;
		if (_ledIndicationVal !== cacheLedIndicationVal)
			changedConfig.led_output_indication = _ledIndicationVal;
		if (brightnessNum !== cacheBrightness) changedConfig.led_brightness = brightnessNum;
		if (swapInputs !== cacheSwapInputs) changedConfig.swap_inputs = swapInputs;

		if (Object.keys(changedConfig).length === 0) return;

		loading = true;
		const res: SettingRequest = await gen2Request(device, 'WALLSWITCH_UI.SetConfig', {
			config: changedConfig,
		});

		if (res.isok) {
			// Merge onto the latest stored config (not the mount-time snapshot) so
			// consecutive saves don't revert previously saved fields in the store.
			const previous = settings.wallswitch_ui ?? {};
			settings.wallswitch_ui = { ...previous, ...changedConfig };
			devices_settings.update_devices_settings(id, settings);
			cacheLedModeVal = _ledModeVal;
			cacheLedIndicationVal = _ledIndicationVal;
			cacheBrightness = brightnessNum;
			cacheSwapInputs = swapInputs;
			toast.open({
				type: 'success',
				title: $format('setting_scc_saved_toast', { default: 'Setting saved successfully.' }),
			});
		} else {
			toast.open({
				type: 'danger',
				title: $format('error_global', { default: 'Something went wrong.' }),
			});
		}
		loading = false;
	};
</script>

<Text styleType="body-small" class="mb-s">
	{$format('ss_led_mode_desc', {
		default: 'Choose what the status LED indicates.',
	})}
</Text>
<Select
	class="mb-s"
	value={ledModeLabel}
	options={ledModeLabels}
	icon={null}
	on:change={changeLedMode}
	id="select-ss-led-mode"
	aria-label={$format('ss_led_mode_label', { default: 'LED mode' })}
/>

<Text styleType="body-small" class="mb-s">
	{$format('ss_led_output_indication_desc', {
		default: 'Choose how the LED reflects the output state.',
	})}
</Text>
<Select
	class="mb-s"
	value={ledIndicationLabel}
	options={ledIndicationLabels}
	icon={null}
	on:change={changeLedIndication}
	id="select-ss-led-indication"
	aria-label={$format('ss_led_output_indication_label', { default: 'LED output indication' })}
/>

<Text styleType="body-small" class="mb-s">
	{$format('ss_led_brightness_desc', {
		default: 'Set the brightness of the status LED.',
	})}
</Text>
<Input
	size="m"
	type="number"
	min={BRIGHTNESS_MIN}
	max={BRIGHTNESS_MAX}
	class="mb-s"
	label={$format('ss_led_brightness_label', { default: 'LED brightness' })}
	bind:value={brightness}
	error={brightnessError}
	info={brightnessInfo}
	id="input-ss-led-brightness"
	aria-label={$format('ss_led_brightness_label', { default: 'LED brightness' })}
/>

<Text styleType="body-small" class="mb-s">
	{$format('ss_swap_inputs_desc', {
		default:
			'Swap the two buttons: the button that turns the output on will turn it off, and the other way around.',
	})}
</Text>
<Toggle bind:checked={swapInputs} size="m" class="mb-s" id="toggle-ss-swap-inputs">
	{$format('ss_swap_inputs_label', { default: 'Swap inputs' })}
</Toggle>

<Button
	{loading}
	class="mt-s"
	size="m"
	width="full"
	disabled={brightnessInvalid}
	on:click={sentRequests}
	aria-label={$format('button_save', { default: 'Save' })}
>
	{$format('button_save', { default: 'Save' })}
</Button>
